import MuleBadge from '../../components/MuleBadge';
import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import SingleSelect, { SingleSelectOption } from '../../components/SingleSelect';
import { OrderDispatcherFilter } from '../../components/filters';
import FilterBar from '../../components/FilterBar';
import Toggle from '../../components/Toggle';
import DateTimePicker, { DateTimeValue } from '../../components/DateTimePicker';
import { useGlobalFilters } from '../../contexts/GlobalFiltersContext';
import ordersService from '../../services/orders.service';
import { config } from '../../config/config';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import dispatchVisibilityService, {
  VisibilityView,
  VisibilityRow,
  VisibilityMove,
  VisibilityBucket,
} from '../../services/dispatchVisibility.service';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

// The three top-level views the page can show.
const VIEW_OPTIONS: { value: VisibilityView; label: string }[] = [
  { value: 'destination', label: 'Destination' },
  { value: 'origin', label: 'By Origin' },
  { value: 'returns', label: 'Returns' },
];

// Move-type filter options. Must match the movetype values the backend includes
// in _BASE_FROM_WHERE (backend/app/api/api_v1/endpoints/dispatch_visibility.py).
const MOVETYPE_OPTIONS: MultiSelectOption[] = [
  { value: 'IMPORT', label: 'Import' },
  { value: 'EXPORT', label: 'Export' },
  { value: 'INBOUND', label: 'Inbound' },
  { value: 'OUTBOUND', label: 'Outbound' },
  { value: 'DOMESTIC', label: 'Domestic' },
  { value: 'REPO', label: 'Repo' },
];

type SectionFilter = 'both' | 'not_dispatched' | 'dispatched';
const SECTION_OPTIONS: SingleSelectOption[] = [
  { value: 'both', label: 'Both' },
  { value: 'not_dispatched', label: 'Not Dispatched' },
  { value: 'dispatched', label: 'Dispatched' },
];

// Human-readable labels for each bucket, used in the drill-down title.
const BUCKET_LABELS: Record<VisibilityBucket, string> = {
  nd_total: 'Not Dispatched — Total',
  nd_triaxle: 'Not Dispatched — Triaxle',
  nd_swing: 'Not Dispatched — Swing',
  nd_reg: 'Not Dispatched — Reg',
  nd_hot: 'Not Dispatched — Hot',
  nd_haz: 'Not Dispatched — Haz',
  d_driver: 'Dispatched — Driver',
  d_no_og: 'Dispatched — No OG',
  d_no_ch: 'Dispatched — No Ch',
};

interface DrillState {
  // One code for a single-location count cell; many for a footer-total click
  // (every location currently visible in the summary table with a count > 0).
  location_codes: number[];
  // Single location's name for a cell drill; '' for a footer-total drill.
  location_name: string;
  bucket: VisibilityBucket;
  // True when opened from a footer total: show the Location column + a total-style title.
  isTotal: boolean;
}

// localStorage key for the Side By Side View toggle.
const SIDE_BY_SIDE_KEY = 'dispatch-visibility-side-by-side';
// localStorage key + bounds for the user-resizable modal-mode drill-down box.
const MODAL_SIZE_KEY = 'dispatch-visibility-modal-size';
const MODAL_MIN_W = 480;
const MODAL_MIN_H = 320;

// Format a Date as a local YYYY-MM-DD string (no UTC shift).
const toYmd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Render an appointment date, blanking out falsy / zero-date sentinels.
function formatApptDate(value: string): string {
  if (!value || value === '0000-00-00') return '';
  return value;
}

function DispatchVisibility() {
  const {
    selectedOrderDispatchers,
    setSelectedOrderDispatchers,
    selectedRegions,
    setSelectedRegions,
    isLoading: globalFiltersLoading,
  } = useGlobalFilters();

  const [view, setView] = useState<VisibilityView>('destination');
  const [sectionFilter, setSectionFilter] = useState<SectionFilter>('both');
  const [selectedMovetypes, setSelectedMovetypes] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const dateStr = useMemo(() => toYmd(selectedDate), [selectedDate]);
  // Side-by-side (split) vs modal drill-down; persisted, defaults on.
  const [sideBySide, setSideBySide] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDE_BY_SIDE_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(SIDE_BY_SIDE_KEY, String(sideBySide));
    } catch {
      /* ignore storage errors */
    }
  }, [sideBySide]);
  // Modal-mode drill-down box size (px), user-resizable and persisted. CSS
  // max-w/max-h clamp it to the viewport, so the stored value can't strand it offscreen.
  const [modalSize, setModalSize] = useState<{ w: number; h: number }>(() => {
    try {
      const raw = localStorage.getItem(MODAL_SIZE_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p?.w === 'number' && typeof p?.h === 'number') return p;
      }
    } catch {
      /* ignore */
    }
    return { w: 896, h: Math.round(window.innerHeight * 0.8) };
  });
  useEffect(() => {
    try {
      localStorage.setItem(MODAL_SIZE_KEY, JSON.stringify(modalSize));
    } catch {
      /* ignore storage errors */
    }
  }, [modalSize]);
  // Drag origin for the resize handle: pointer + box size captured on pointer-down.
  const resizeStart = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const [rows, setRows] = useState<VisibilityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<DrillState | null>(null);
  const {
    panelRef: drillPanelRef,
    panelStyle: drillPanelStyle,
    dragHandleProps: drillDragHandleProps,
  } = useModalDrag(drill !== null);
  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);
  // Pro Detail slide-over opened from a pro number in the drill-down panel.
  const [selectedProNumber, setSelectedProNumber] = useState<number | null>(null);

  const fetchAbortRef = useRef<AbortController | null>(null);

  // Fetch region filter options once on mount. Fail quietly so the page still
  // works if the regions endpoint is unavailable.
  useEffect(() => {
    let cancelled = false;
    ordersService
      .getRegions()
      .then((res) => {
        if (cancelled) return;
        const opts = (res.data.regions || []).map((r) => ({ value: r, label: r }));
        setRegionOptions(opts);
      })
      .catch(() => {
        // Silently ignore — filter just shows no options.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the summary on mount and whenever the view changes. Abort any
  // in-flight request so a slow earlier view can't clobber the current one.
  useEffect(() => {
    // Wait for the saved global filters to restore — without this gate the
    // mount fetched with default filters and the restore immediately refired.
    if (globalFiltersLoading) return;
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setLoading(true);
    setError(null);

    dispatchVisibilityService
      .getSummary(
        view,
        {
          order_dispatchers: selectedOrderDispatchers,
          regions: selectedRegions,
          movetypes: selectedMovetypes,
          date: dateStr,
        },
        controller.signal,
      )
      .then((data) => {
        setRows(data);
      })
      .catch((err: any) => {
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
        setError(err.response?.data?.detail || 'Failed to load dispatch visibility summary');
      })
      .finally(() => {
        // Only clear loading if this controller is still the active one.
        if (fetchAbortRef.current === controller) {
          setLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [globalFiltersLoading, view, selectedOrderDispatchers, selectedRegions, selectedMovetypes, dateStr]);

  // Renders a count cell: muted 0, or a clickable button that opens the
  // drill-down for the given bucket on this row.
  const makeCountColumn = (
    bucket: VisibilityBucket,
    header: string,
    tint?: 'sky' | 'purple',
  ): ColumnDef<VisibilityRow> => ({
    accessorKey: bucket,
    header,
    enableSorting: true,
    enableColumnFilter: false,
    meta: {
      ...(tint === 'sky' && {
        headerBgClass: 'bg-sky-50 dark:bg-sky-900/30',
        cellBgClass: 'bg-sky-50 dark:bg-sky-900/20',
        cellBgClassZebra: 'bg-sky-100 dark:bg-sky-900/40',
      }),
      ...(tint === 'purple' && {
        headerBgClass: 'bg-purple-50 dark:bg-purple-900/30',
        cellBgClass: 'bg-purple-50 dark:bg-purple-900/20',
        cellBgClassZebra: 'bg-purple-100 dark:bg-purple-900/40',
      }),
    },
    footer: ({ table }) => {
      const rows = table.getFilteredRowModel().rows;
      const total = rows.reduce((sum, r) => sum + ((r.original as VisibilityRow)[bucket] || 0), 0);
      if (total <= 0) return <span className="text-gray-400 dark:text-gray-500">0</span>;
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            // Drill into every currently-visible location that contributes to this
            // total, so the modal's rows match the number being clicked.
            const codes = rows
              .filter((r) => ((r.original as VisibilityRow)[bucket] || 0) > 0)
              .map((r) => (r.original as VisibilityRow).location_code);
            setDrill({ location_codes: codes, location_name: '', bucket, isTotal: true });
          }}
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
        >
          {total.toLocaleString()}
        </button>
      );
    },
    cell: ({ row }) => {
      const value = row.original[bucket] as number;
      if (!value) {
        return <span className="text-gray-400 dark:text-gray-500">0</span>;
      }
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setDrill({
              location_codes: [row.original.location_code],
              location_name: row.original.location_name,
              bucket,
              isTotal: false,
            });
          }}
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline font-medium cursor-pointer"
        >
          {value}
        </button>
      );
    },
  });

  const columns = useMemo<ColumnDef<VisibilityRow>[]>(() => [
    {
      accessorKey: 'location_name',
      header: 'Location',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ getValue }) => (
        <span className="text-sm text-body">{getValue<string>()}</span>
      ),
      footer: ({ table }) => {
        const n = table.getFilteredRowModel().rows.length;
        return <span className="text-sm">Total ({n.toLocaleString()})</span>;
      },
    },
    ...(sectionFilter !== 'dispatched' ? [{
      id: 'not_dispatched',
      header: 'Not Dispatched',
      meta: { headerBgClass: 'bg-purple-50 dark:bg-purple-900/30' },
      columns: [
        makeCountColumn('nd_total', 'Total', 'purple'),
        makeCountColumn('nd_triaxle', 'Triaxle', 'purple'),
        makeCountColumn('nd_swing', 'Swing', 'purple'),
        makeCountColumn('nd_reg', 'Reg', 'purple'),
        makeCountColumn('nd_hot', 'Hot', 'purple'),
        makeCountColumn('nd_haz', 'Haz', 'purple'),
      ],
    }] : []),
    ...(sectionFilter !== 'not_dispatched' ? [{
      id: 'dispatched',
      header: 'Dispatched',
      meta: { headerBgClass: 'bg-sky-50 dark:bg-sky-900/30' },
      columns: [
        makeCountColumn('d_driver', 'Driver', 'sky'),
        makeCountColumn('d_no_og', 'No OG', 'sky'),
        makeCountColumn('d_no_ch', 'No Ch', 'sky'),
      ],
    }] : []),
  ], [sectionFilter]);

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Filter bar (Order Dispatcher + Region) with the view toggle right-aligned */}
      <FilterBar label="Filters:">
        <DateTimePicker
          mode="date"
          selectionType="single"
          inputVariant="compact"
          autoApply
          labelType="none"
          value={selectedDate}
          onChange={(v: Date | DateTimeValue | null) => {
            const d = v instanceof Date ? v : v?.date;
            // This filter always needs a date; clearing (X) reverts to today so the
            // picker display and the filtered data stay in sync.
            setSelectedDate(d ?? new Date());
          }}
        />
        <OrderDispatcherFilter
          value={selectedOrderDispatchers}
          onChange={setSelectedOrderDispatchers}
        />
        <MultiSelect
          options={regionOptions}
          value={selectedRegions}
          onChange={setSelectedRegions}
          placeholder="All Regions"
          showClearOption={true}
          clearOptionLabel="All Regions"
          enableSearch={true}
          searchPlaceholder="Search regions..."
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={MOVETYPE_OPTIONS}
          value={selectedMovetypes}
          onChange={setSelectedMovetypes}
          placeholder="All Move Types"
          showClearOption={true}
          clearOptionLabel="All Move Types"
          size="sm"
          className="min-w-[150px]"
        />
        <SingleSelect
          options={SECTION_OPTIONS}
          value={sectionFilter}
          onChange={(v) => setSectionFilter((v as SectionFilter) || 'both')}
          size="sm"
          className="min-w-[150px]"
        />
        <FilterBar.Spacer />
        <FilterBar.Group label="Side By Side View">
          <Toggle
            checked={sideBySide}
            onChange={(v) => {
              // Clear any selection so flipping layouts doesn't pop a modal open
              // (modal mode) or strand a stale panel.
              setDrill(null);
              setSideBySide(v);
            }}
            ariaLabel="Side By Side View"
            size="sm"
          />
        </FilterBar.Group>
        {/* 3-way view toggle */}
        <div
          role="group"
          aria-label="View"
          className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden"
        >
          {VIEW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              aria-pressed={view === opt.value}
              onClick={() => setView(opt.value)}
              className={`px-4 py-1.5 text-sm font-medium transition-colors border-r last:border-r-0 border-gray-300 dark:border-gray-600 ${
                view === opt.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-panel text-label hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </FilterBar>

      {/* Body. Side-by-side: summary (left 50%) | drill-down moves (right 50%).
          Modal mode: summary full-width; the drill-down opens in a modal below. */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Summary counts */}
        <div
          className={`flex flex-col min-h-0 overflow-hidden gap-4 p-4 ${
            sideBySide ? 'w-1/2 border-r border-theme-border-primary' : 'w-full'
          }`}
        >
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          <TanTable
            data={rows}
            columns={columns}
            loading={loading}
            settingsKey="dispatch-visibility"
            cardTitle="Dispatch Visibility"
            searchPlaceholder="Search locations..."
            enableColumnFilters={true}
            enableSearch={true}
            enableExcelExport={true}
            defaultPageSize={100}
            fillContainer={true}
            showColumnFooter={true}
          />
        </div>

        {/* Right panel — side-by-side mode only */}
        {sideBySide && (
          <div className="w-1/2 flex flex-col min-h-0 overflow-hidden">
            {drill ? (
              <DrillDownPanel
                view={view}
                drill={drill}
                apptDate={dateStr}
                orderDispatchers={selectedOrderDispatchers}
                regions={selectedRegions}
                movetypes={selectedMovetypes}
                onProClick={setSelectedProNumber}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center p-6">
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Select a count to view its moves.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal mode — drill-down in a centered modal (z-90; the Pro Detail
          slide-over at z-100 layers above it). */}
      {!sideBySide && drill &&
        createPortal(
          <div
            className="fixed inset-0 z-[90] flex items-center justify-center bg-scrim/50 p-4"
            onClick={() => setDrill(null)}
          >
            <div
              ref={drillPanelRef}
              className="relative flex flex-col max-w-[95vw] max-h-[90vh] bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden"
              style={{ width: modalSize.w, height: modalSize.h, ...drillPanelStyle }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader
                title={
                  drill.isTotal
                    ? BUCKET_LABELS[drill.bucket]
                    : `${drill.location_name || '(Unnamed)'} - ${BUCKET_LABELS[drill.bucket]}`
                }
                onClose={() => setDrill(null)}
                closeLabel="Close moves"
                dragHandleProps={drillDragHandleProps}
              />
              <DrillDownPanel
                view={view}
                drill={drill}
                apptDate={dateStr}
                orderDispatchers={selectedOrderDispatchers}
                regions={selectedRegions}
                movetypes={selectedMovetypes}
                onProClick={setSelectedProNumber}
              />
              {/* Resize handle. The box is centered, so growing it by 2× the pointer
                  delta keeps the bottom-right corner tracking the cursor. */}
              <div
                role="separator"
                aria-label="Resize"
                onPointerDown={(e) => {
                  e.preventDefault();
                  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                  resizeStart.current = { x: e.clientX, y: e.clientY, w: modalSize.w, h: modalSize.h };
                }}
                onPointerMove={(e) => {
                  const s = resizeStart.current;
                  if (!s) return;
                  const maxW = Math.round(window.innerWidth * 0.95);
                  const maxH = Math.round(window.innerHeight * 0.9);
                  const w = Math.min(maxW, Math.max(MODAL_MIN_W, s.w + (e.clientX - s.x) * 2));
                  const h = Math.min(maxH, Math.max(MODAL_MIN_H, s.h + (e.clientY - s.y) * 2));
                  setModalSize({ w, h });
                }}
                onPointerUp={(e) => {
                  resizeStart.current = null;
                  (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
                }}
                className="absolute bottom-0 right-0 z-10 flex h-5 w-5 cursor-nwse-resize items-end justify-end p-0.5"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" className="pointer-events-none text-gray-400 dark:text-gray-500">
                  <path d="M9 1 L1 9 M9 5 L5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Pro Detail slide-over (z-100) opens over the whole page. */}
      <ProDetailSlideOver
        pronumber={selectedProNumber}
        isOpen={selectedProNumber !== null}
        onClose={() => setSelectedProNumber(null)}
      />
    </div>
  );
}

interface DrillDownPanelProps {
  view: VisibilityView;
  drill: DrillState;
  apptDate: string;
  orderDispatchers: string[];
  regions: string[];
  movetypes: string[];
  onProClick: (pronumber: number) => void;
}

function DrillDownPanel({ view, drill, apptDate, orderDispatchers, regions, movetypes, onProClick }: DrillDownPanelProps) {
  // Any orders-scope registry field can be added as a column. The selection follows
  // the same key the layout does, so a footer-total drill and a single-location drill
  // keep separate column sets — the two are deliberately different views.
  const settingsKey = drill.isTotal ? 'dispatch-visibility-moves-all' : 'dispatch-visibility-moves';
  const fieldSelection = useAdditionalFieldSelection(settingsKey);
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const [moves, setMoves] = useState<VisibilityMove[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stable dependency key for the (variable-length) location-code list.
  const codesKey = drill.location_codes.join(',');

  useEffect(() => {
    // The saved selection is part of the request; going early just fetches twice.
    if (!fieldSelection.ready) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    dispatchVisibilityService
      .getMoves(
        {
          view,
          location_codes: drill.location_codes,
          appt_date: apptDate,
          bucket: drill.bucket,
          order_dispatchers: orderDispatchers,
          regions,
          movetypes,
          additional_fields: fieldSelection.additionalFields,
        },
        controller.signal,
      )
      .then((data) => setMoves(data))
      .catch((err: any) => {
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
        setError(err.response?.data?.detail || 'Failed to load moves');
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, codesKey, apptDate, drill.bucket, orderDispatchers, regions, movetypes, fieldSelection.ready, additionalFieldsKey]);

  const baseColumns = useMemo<ColumnDef<VisibilityMove>[]>(() => [
    {
      accessorKey: 'pronumber',
      header: 'PRO#',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const pronumber = String(row.original.pronumber ?? '');
        const legnum = row.original.legnum;
        if (!pronumber) return '';
        const legacyCmsUrl = config.LEGACY_CMS_URL;
        return (
          <span className="inline-flex items-center gap-1.5 text-sm">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onProClick(Number(pronumber));
              }}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
              title="Open Pro detail"
            >
              {pronumber}{legnum ? `-${legnum}` : ''}
            </button>
            <a
              href={`${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Open in legacy CMS"
              aria-label="Open in legacy CMS"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
            </a>
          </span>
        );
      },
    },
    {
      accessorKey: 'driver',
      meta: { exportValue: (row: { original: VisibilityMove }) => `${row.original.driver || ''}${row.original.isMule ? ' (Mule)' : ''}` },
      header: 'Driver',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ getValue, row }) => {
        const value = getValue<string>();
        return value ? (
          <span className="text-sm text-body">{value}<MuleBadge isMule={row.original.isMule} /></span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">-</span>
        );
      },
    },
    {
      accessorKey: 'movetype',
      header: 'Move Type',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ getValue }) => (
        <span className="text-sm text-body">{getValue<string>()}</span>
      ),
    },
    {
      accessorKey: 'container',
      header: 'Container #',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ getValue }) => {
        const value = getValue<string>();
        return value ? (
          <span className="text-sm text-body">{value}</span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">-</span>
        );
      },
    },
    {
      accessorKey: 'chassisid',
      header: 'Chassis',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ getValue }) => {
        const value = getValue<string>();
        return value ? (
          <span className="text-sm text-body">{value}</span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">-</span>
        );
      },
    },
    {
      accessorKey: 'appt_date',
      header: 'Appt Date',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ getValue }) => (
        <span className="text-sm text-body">
          {formatApptDate(getValue<string>())}
        </span>
      ),
    },
    // Footer-total drill-downs span every visible location, so show which one each
    // move belongs to. Single-location cell drills omit this (it would be constant).
    ...(drill.isTotal ? [{
      accessorKey: 'location_name',
      header: 'Location',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ getValue }) => (
        <span className="text-sm text-body">{getValue<string>()}</span>
      ),
    }] as ColumnDef<VisibilityMove>[] : []),
  ], [onProClick, drill.isTotal]);

  // Rows are (pronumber, legnum) moves — a pro drilled in on several legs is several
  // rows — so an added order column repeats down them rather than blanking.
  const { columns, availableFields } = useAdditionalFieldColumns<VisibilityMove>(
    fieldSelection, { baseColumns },
  );

  // Table title. Single-location drill: "DAL TILE - 383 - Not Dispatched — Total".
  // Footer-total drill: just the bucket label (the row count is shown by TanTable).
  // The date is shown by the top date picker, so it's omitted here.
  const panelTitle = drill.isTotal
    ? BUCKET_LABELS[drill.bucket]
    : `${drill.location_name || '(Unnamed)'} - ${BUCKET_LABELS[drill.bucket]}`;

  return (
    <div className="flex-1 flex flex-col p-4 overflow-hidden">
      {error ? (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      ) : (
        <TanTable
          // Distinct key matching the conditional settingsKey: a footer-total drill and a
          // single-location cell drill reuse this one TanTable (in side-by-side mode the
          // DrillDownPanel stays mounted while `drill` changes), so without a key that
          // tracks drill.isTotal the instance keeps the previous drill's layout and its
          // autosave writes it into the other key. Key = fresh mount when isTotal flips;
          // seedLayoutFromCache applies the cached layout pre-paint (no flash).
          key={settingsKey}
          data={moves}
          columns={columns}
          availableFields={availableFields}
          additionalFields={fieldSelection.additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          loading={loading}
          settingsKey={settingsKey}
          seedLayoutFromCache
          cardTitle={panelTitle}
          searchPlaceholder="Search moves..."
          enableColumnFilters={true}
          enableSearch={true}
          fillContainer={true}
        />
      )}
    </div>
  );
}

export default DispatchVisibility;
